// Mock data for Compliance Requirements View based on new API response structure
import { 
  ComplianceRequirementsViewResponse, 
  ComplianceStatus
} from '../types/complianceRequirementsView';

// Generate unique GUIDs for mock data
const generateGUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Utility to generate realistic dates
const getDateString = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

export const mockComplianceRequirementsViewData: ComplianceRequirementsViewResponse = {
  entitySummary: {
    status: "Warning", // Overall status indicating some requirements need attention
    score: 78 // Overall compliance score out of 100
  },
  scopes: [
    // EverDriven Scope
    {
      scopeType: "EverDriven",
      scopeId: generateGUID(),
      scopeName: "EverDriven Requirements",
      roles: [
        {
          role: "Driver",
          status: "Warning",
          score: 82,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Driver Safety Training",
                description: "Mandatory safety training for all drivers covering vehicle operation, emergency procedures, and student safety protocols.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Drivers" },
                  { appliesToId: generateGUID(), appliesToName: "Commercial Vehicle Operators" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual Renewal",
                  duration: 365,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Days",
                  expirationDate: getDateString(45),
                  approvalNeededName: "Training Department Approval"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(30),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-14),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Pending Review",
              complianceStatus: "Warning"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Vehicle Inspection Certification",
                description: "Annual vehicle inspection and safety certification for all fleet vehicles.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Fleet Operators" },
                  { appliesToId: generateGUID(), appliesToName: "Vehicle Inspectors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(120),
                  approvalNeededName: "Fleet Manager Approval"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(90),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-7),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Submitted",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Emergency Response Procedures",
                description: "Training and certification on emergency response procedures including medical emergencies, vehicle breakdowns, and evacuation protocols.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 6,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Months",
                  expirationDate: getDateString(-15),
                  approvalNeededName: "Safety Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(-5),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Daily"
              },
              evidenceStatus: "Not Submitted",
              complianceStatus: "Expired"
            }
          ]
        },
        {
          role: "Monitor",
          status: "Compliant",
          score: 95,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Student Supervision Training",
                description: "Comprehensive training on student supervision, behavior management, and safety protocols for monitors.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" },
                  { appliesToId: generateGUID(), appliesToName: "Safety Personnel" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(200),
                  approvalNeededName: "Training Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(180),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-10),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "First Aid Certification",
                description: "Current first aid and CPR certification for all monitors to handle medical emergencies.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 2,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(60),
                  approvalNeededName: "Medical Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(45),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-5),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Submitted",
              complianceStatus: "Warning"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "EverDriven Safety Protocols",
                description: "Comprehensive understanding of EverDriven's specific safety protocols and incident reporting procedures.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" },
                  { appliesToId: generateGUID(), appliesToName: "Safety Personnel" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(180),
                  approvalNeededName: "Safety Manager"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(160),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-14),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Background Check Renewal",
                description: "Annual background check renewal for all monitor positions as required by EverDriven policy.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Integration"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(30),
                  approvalNeededName: "HR Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "EverDriven Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(15),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "In Progress",
              complianceStatus: "Warning"
            }
          ]
        }
      ]
    },
    // Michigan State Scope
    {
      scopeType: "State",
      scopeId: generateGUID(),
      scopeName: "Michigan State Requirements",
      roles: [
        {
          role: "Driver",
          status: "Failed",
          score: 65,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Michigan Commercial Driver License",
                description: "Valid Michigan CDL with appropriate endorsements for school bus operation.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "School Bus Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "License Renewal",
                  duration: 4,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(730),
                  approvalNeededName: "Michigan DMV"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Michigan State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(700),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Michigan Background Check",
                description: "State-mandated criminal background check including fingerprinting and driving record review.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All School Personnel" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Integration"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Periodic Review",
                  duration: 3,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(-30),
                  approvalNeededName: "Michigan State Police"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Michigan State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(-15),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-60),
                notificationFrequency: "Daily"
              },
              evidenceStatus: "Processing",
              complianceStatus: "Expired"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Michigan Physical Examination",
                description: "Annual DOT physical examination required for commercial drivers.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "CDL Holders" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(90),
                  approvalNeededName: "Certified Medical Examiner"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Michigan State Requirements"
                },
                impacting: true,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(75),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-15),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Rejected",
              complianceStatus: "Rejected"
            }
          ]
        },
        {
          role: "Monitor",
          status: "Warning",
          score: 75,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Michigan Child Protection Training",
                description: "State-required training on child protection policies and mandatory reporting requirements.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "School Personnel" },
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 2,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(15),
                  approvalNeededName: "Michigan Department of Education"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Michigan State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(10),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-45),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Pending Review",
              complianceStatus: "Warning"
            }
          ]
        }
      ]
    },
    // New York State Scope
    {
      scopeType: "State",
      scopeId: generateGUID(),
      scopeName: "New York State Requirements",
      roles: [
        {
          role: "Driver",
          status: "Warning",
          score: 70,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "NYS Transportation Authority Permit",
                description: "New York State transportation authority permit for school bus operations.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "School Bus Operators" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(150),
                  approvalNeededName: "NYS Department of Transportation"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "New York State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(130),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-20),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Submitted",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "NYS Drug & Alcohol Testing",
                description: "Mandatory pre-employment and random drug and alcohol testing as required by New York State.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "CDL Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Integration"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Ongoing",
                  duration: 6,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Months",
                  expirationDate: getDateString(45),
                  approvalNeededName: "Certified Testing Facility"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "New York State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(30),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-15),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Pending",
              complianceStatus: "Warning"
            }
          ]
        }
      ]
    },
    // California State Scope
    {
      scopeType: "State",
      scopeId: generateGUID(),
      scopeName: "California State Requirements",
      roles: [
        {
          role: "Driver",
          status: "Compliant",
          score: 92,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "California Commercial Driver License",
                description: "California CDL with school bus endorsement and passenger endorsement.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "School Bus Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "License Renewal",
                  duration: 5,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(900),
                  approvalNeededName: "California DMV"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "California State Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(870),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-90),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            }
          ]
        }
      ]
    },
    // Michigan Districts Scope
    {
      scopeType: "District",
      scopeId: generateGUID(),
      scopeName: "Ann Arbor School District",
      roles: [
        {
          role: "Driver",
          status: "Warning",
          score: 68,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "District Safety Orientation",
                description: "Mandatory orientation covering district-specific safety policies, routes, and procedures.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "New Drivers" },
                  { appliesToId: generateGUID(), appliesToName: "Annual Refresher" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(25),
                  approvalNeededName: "District Transportation Director"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Ann Arbor School District"
                },
                impacting: true,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(20),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-10),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "In Progress",
              complianceStatus: "Warning"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Immunization Records",
                description: "Current immunization records including COVID-19, flu, and standard vaccinations.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Personnel" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(-10),
                  approvalNeededName: "District Health Office"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Ann Arbor School District"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(-5),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-40),
                notificationFrequency: "Daily"
              },
              evidenceStatus: "Missing",
              complianceStatus: "Expired"
            }
          ]
        },
        {
          role: "Monitor",
          status: "Compliant",
          score: 90,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Student Behavior Management",
                description: "Training on district policies for managing student behavior and de-escalation techniques.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Bus Monitors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(180),
                  approvalNeededName: "District Training Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Ann Arbor School District"
                },
                impacting: false,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(160),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            }
          ]
        }
      ]
    },
    // Lansing School District
    {
      scopeType: "District",
      scopeId: generateGUID(),
      scopeName: "Lansing School District",
      roles: [
        {
          role: "Driver",
          status: "Failed",
          score: 55,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "District Technology Training",
                description: "Training on district technology systems including GPS, communication devices, and student management systems.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 6,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Months",
                  expirationDate: getDateString(-45),
                  approvalNeededName: "IT Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Lansing School District"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(-30),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-75),
                notificationFrequency: "Daily"
              },
              evidenceStatus: "Not Submitted",
              complianceStatus: "Failed"
            }
          ]
        }
      ]
    },
    // New York Districts Scope
    {
      scopeType: "District",
      scopeId: generateGUID(),
      scopeName: "NYC Department of Education",
      roles: [
        {
          role: "Driver",
          status: "Warning",
          score: 72,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "NYC Safety Protocols",
                description: "NYC-specific safety protocols including urban driving conditions and emergency procedures.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "NYC Drivers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(40),
                  approvalNeededName: "NYC DOE Transportation"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "NYC Department of Education"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(30),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-15),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Pending",
              complianceStatus: "Warning"
            }
          ]
        }
      ]
    },
    // Vehicle Scope
    {
      scopeType: "Vehicle",
      scopeId: generateGUID(),
      scopeName: "Fleet Vehicle Requirements",
      roles: [
        {
          role: "Driver",
          status: "Compliant",
          score: 88,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Vehicle Safety Inspection",
                description: "Annual vehicle safety inspection covering brakes, lights, emergency equipment, and structural integrity.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Fleet Vehicles" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(240),
                  approvalNeededName: "Certified Inspector"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(220),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Insurance Documentation",
                description: "Current comprehensive insurance coverage including liability, collision, and passenger protection.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Vehicles" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Integration"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Policy Renewal",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(365),
                  approvalNeededName: "Insurance Provider"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(340),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Current",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Vehicle Registration",
                description: "Current vehicle registration and licensing documentation for all fleet vehicles.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Fleet Vehicles" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(150),
                  approvalNeededName: "Fleet Manager"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(130),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-21),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Approved",
              complianceStatus: "Compliant"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Emergency Equipment Check",
                description: "Monthly verification of emergency equipment including first aid kits, fire extinguishers, and emergency exits.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All School Buses" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Monthly",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Months",
                  expirationDate: getDateString(15),
                  approvalNeededName: "Safety Inspector"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(10),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-5),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Overdue",
              complianceStatus: "Failed"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Vehicle Maintenance Records",
                description: "Comprehensive maintenance records including oil changes, brake inspections, and routine servicing.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Fleet Vehicles" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: false,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Ongoing",
                  duration: 0,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Continuous",
                  expirationDate: "",
                  approvalNeededName: "Fleet Manager"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: false,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(0),
                expectedCompletionDateEnabled: false,
                notificationStartDate: getDateString(-30),
                notificationFrequency: "As needed"
              },
              evidenceStatus: "Current",
              complianceStatus: "Compliant"
            }
          ]
        },
        {
          role: "Fleet Manager",
          status: "Warning",
          score: 75,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Fleet Management Certification",
                description: "Professional certification in fleet management including vehicle procurement, maintenance scheduling, and cost optimization.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Fleet Managers" },
                  { appliesToId: generateGUID(), appliesToName: "Transportation Supervisors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Document Upload"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Triennial",
                  duration: 3,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(45),
                  approvalNeededName: "Transportation Director"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(30),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-15),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Pending Review",
              complianceStatus: "Warning"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Vehicle Acquisition Training",
                description: "Training on EverDriven's vehicle acquisition policies, vendor management, and procurement procedures.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "Fleet Managers" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 2,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(365),
                  approvalNeededName: "Procurement Department"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Fleet Vehicle Requirements"
                },
                impacting: false,
                exceptionsAllowed: true,
                expectedCompletionDate: getDateString(350),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-14),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "Not Started",
              complianceStatus: "NotStarted"
            }
          ]
        }
      ]
    },
    // Shared/Multi-Scope Requirements
    {
      scopeType: "Shared",
      scopeId: generateGUID(),
      scopeName: "Cross-Functional Requirements",
      roles: [
        {
          role: "All Personnel",
          status: "Warning",
          score: 72,
          requirements: [
            {
              requirement: {
                id: generateGUID(),
                name: "Data Privacy and Security Training",
                description: "Mandatory training on data privacy, FERPA compliance, and cybersecurity best practices for all staff.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Staff" },
                  { appliesToId: generateGUID(), appliesToName: "Contractors" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Form"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Annual",
                  duration: 1,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(60),
                  approvalNeededName: "IT Security Officer"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Cross-Functional Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(45),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-14),
                notificationFrequency: "Weekly"
              },
              evidenceStatus: "Overdue",
              complianceStatus: "Failed"
            },
            {
              requirement: {
                id: generateGUID(),
                name: "Emergency Preparedness Plan",
                description: "Understanding of company-wide emergency preparedness procedures including natural disasters, security threats, and medical emergencies.",
                appliesTo: [
                  { appliesToId: generateGUID(), appliesToName: "All Personnel" }
                ],
                requirementType: {
                  typeId: generateGUID(),
                  typeName: "Digital Document/Attestation"
                },
                expiration: {
                  expires: true,
                  expirationTypeId: generateGUID(),
                  expirationTypeName: "Biannual",
                  duration: 2,
                  timePeriodId: generateGUID(),
                  timePeriodName: "Years",
                  expirationDate: getDateString(180),
                  approvalNeededName: "Safety Director"
                },
                required: true,
                status: {
                  statusId: generateGUID(),
                  statusName: "Active"
                },
                scope: {
                  scopeId: generateGUID(),
                  scopeName: "Cross-Functional Requirements"
                },
                impacting: true,
                exceptionsAllowed: false,
                expectedCompletionDate: getDateString(160),
                expectedCompletionDateEnabled: true,
                notificationStartDate: getDateString(-21),
                notificationFrequency: "Monthly"
              },
              evidenceStatus: "In Progress",
              complianceStatus: "Warning"
            }
          ]
        }
      ]
    }
  ]
};

// Mock service class for compatibility with existing code
class MockComplianceRequirementsViewService {
  async getComplianceRequirementsData(): Promise<ComplianceRequirementsViewResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    return mockComplianceRequirementsViewData;
  }

  async getRequirementsByScope(scopeType: string): Promise<ComplianceRequirementsViewResponse> {
    const filteredData = {
      ...mockComplianceRequirementsViewData,
      scopes: mockComplianceRequirementsViewData.scopes.filter(scope => 
        scope.scopeType.toLowerCase() === scopeType.toLowerCase()
      )
    };
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return filteredData;
  }

  async getRequirementsByRole(role: string): Promise<ComplianceRequirementsViewResponse> {
    const filteredData = {
      ...mockComplianceRequirementsViewData,
      scopes: mockComplianceRequirementsViewData.scopes.map(scope => ({
        ...scope,
        roles: scope.roles.filter(r => r.role.toLowerCase() === role.toLowerCase())
      })).filter(scope => scope.roles.length > 0)
    };
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return filteredData;
  }

  async getRequirementsByComplianceStatus(status: ComplianceStatus): Promise<ComplianceRequirementsViewResponse> {
    const filteredData = {
      ...mockComplianceRequirementsViewData,
      scopes: mockComplianceRequirementsViewData.scopes.map(scope => ({
        ...scope,
        roles: scope.roles.map(role => ({
          ...role,
          requirements: role.requirements.filter(req => req.complianceStatus === status)
        })).filter(role => role.requirements.length > 0)
      })).filter(scope => scope.roles.length > 0)
    };
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return filteredData;
  }
}

export const mockComplianceRequirementsViewService = new MockComplianceRequirementsViewService();
